import { array, option } from 'fp-ts';
import { pipe } from 'fp-ts/function';
import { useMemo } from 'react';

import { ModalState, useModalsContext } from '../contexts';

function filterState<T, S extends ModalState>(
  guard: (modalState?: ModalState) => modalState is S,
  pluck: (state: S) => T,
  modalState?: ModalState,
): T {
  return pipe(
    modalState,
    option.fromNullable,
    option.filter(guard), // ensure we have a Sub state
    option.map(pluck),
    option.filter((v) => Array.isArray(v)),
    option.filter(array.isNonEmpty), // filter out cases where the array is missing or empty
    option.getOrElse(() => [] as T), // fallback to an empty array
  );
}

export function useFilteredModalState<T, S extends ModalState>(
  guard: (modalState?: ModalState) => modalState is S,
  pluck: (state: S) => T,
): T {
  const { modalState } = useModalsContext();

  return useMemo(() => filterState(guard, pluck, modalState), [modalState, guard, pluck]);
}
